import React, { FC, useCallback, useContext, useEffect } from 'react';
import {
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from 'reactstrap';
import { MaterialIcon } from '../../../components';
import { useTranslation } from '../../../translations';
import { CashierContext } from '../cashiercontext';
import { LabelPrinterInfo, ZebraState } from './reducer';

export const ConfigureZebraPrinter: FC = () => {
  const { ts } = useTranslation();
  const { state, dispatch } = useContext(CashierContext);

  const refreshAvailablePrinters = useCallback(() => {
    dispatch({
      type: 'ZEBRA_REFRESH_AVAILABLEPRINTERS',
      source: 'zebra',
    });
  }, [dispatch]);

  useEffect(() => {
    if (!state.zebraPrinter?.manager) {
      return;
    }

    state.zebraPrinter.manager.addEventListener('connectedDevice', refreshAvailablePrinters);
    state.zebraPrinter.manager.addEventListener('disconnectedDevice', refreshAvailablePrinters);
    refreshAvailablePrinters();

    return () => {
      state.zebraPrinter?.manager.removeEventListener('connectedDevice', refreshAvailablePrinters);
      state.zebraPrinter?.manager.removeEventListener(
        'disconnectedDevice',
        refreshAvailablePrinters,
      );
    };
  }, [state.zebraPrinter?.manager, refreshAvailablePrinters]);

  if (!state.zebraPrinter) {
    return null;
  }

  return (
    <div>
      <h4>
        <div>{ts('available_badge_printers')}</div>
        <UncontrolledDropdown>
          <DropdownToggle caret>{ts('add_manually')}</DropdownToggle>
          <DropdownMenu>
            <DropdownItem
              onClick={() => {
                dispatch({ type: 'ZEBRA_ADD_PRINTER', source: 'zebra' });
                refreshAvailablePrinters();
              }}
            >
              {ts('__Add zebra printer')}
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      </h4>
      <div>
        {state.zebraPrinter.printers.length === 0 && (
          <div className="print-settings-emptystate">{ts('no_other_badge_printers_available')}</div>
        )}
        {state.zebraPrinter.printers.map((printer) => {
          return <PrinterItem key={printer.serial} printer={printer} state={state.zebraPrinter!} />;
        })}
      </div>
    </div>
  );
};

interface PrinterItemProps {
  readonly printer: LabelPrinterInfo;
  readonly state: ZebraState;
}

const PrinterItem: FC<PrinterItemProps> = ({ state, printer }) => {
  const { ts } = useTranslation();
  const { dispatch } = useContext(CashierContext);

  const setPrimary = () => {
    dispatch({
      type: 'ZEBRA_SET_SLOT',
      slot: 'primary',
      serial: printer.serial,
      source: 'zebra',
    });
  };

  const setSecondary = () => {
    dispatch({
      type: 'ZEBRA_SET_SLOT',
      slot: 'secondary',
      serial: printer.serial,
      source: 'zebra',
    });
  };

  return (
    <div className="print-settings-printer">
      <div className="print-settings-printer-name">
        <div>
          <strong>{printer.name}</strong>
        </div>
        <div>{printer.type}</div>
      </div>
      <div>
        <UncontrolledDropdown>
          <DropdownToggle color="link">
            <MaterialIcon name="settings" small />
          </DropdownToggle>
          <DropdownMenu>
            <DropdownItem>{ts('configure_printer')}</DropdownItem>
            <DropdownItem
              onClick={() => {
                dispatch({
                  type: 'ZEBRA_TEST_PRINT',
                  serial: printer.serial,
                  source: 'zebra',
                });
              }}
            >
              {ts('test_print')}
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
        <Button color="primary" onClick={setPrimary}>
          {ts(state.primaryPrinter === printer.serial ? 'active_as_primary' : 'set_as_primary')}
        </Button>
        <Button color="secondary" onClick={setSecondary} outline>
          {ts(
            state.secondaryPrinter === printer.serial ? 'active_as_secondary' : 'set_as_secondary',
          )}
        </Button>
      </div>
    </div>
  );
};
